/**
 * Analitik Halaman — behaviour on imported sales pages. Shared between the
 * storefront (which hands the tracker its attribution), the server (which stores
 * and aggregates the beacons) and the operator console (which reads them).
 */

/** Query keys the storefront forwards into the framed copy. Nothing else in the
 *  storefront URL (affiliate codes, checkout tokens, …) ever leaves the page. */
const FORWARDED_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "fbclid",
  "gclid",
  "gbraid",
  "wbraid",
  "ttclid",
];

/**
 * The attribution half of a storefront query string, for the `insights:ctx`
 * reply. Click ids are reduced to a presence flag: the tracker only needs to know
 * an ad sent the visit, not which click.
 */
export function attributionSearch(search: string): string {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search);
  } catch {
    return "";
  }
  const out = new URLSearchParams();
  for (const key of FORWARDED_PARAMS) {
    const value = params.get(key);
    if (!value) continue;
    out.set(key, key.startsWith("utm_") ? value.slice(0, 100) : "1");
  }
  return out.toString();
}

/** A referrer reduced to its origin — the tracker only needs the host. */
export function referrerOrigin(referrer: string): string {
  try {
    return referrer ? new URL(referrer).origin : "";
  } catch {
    return "";
  }
}

export type InsightsDevice = "mobile" | "tablet" | "desktop";
export type InsightsClickKind = "cta" | "link" | "dead" | "other";

export interface InsightsTotals {
  sessions: number;
  visitors: number;
  returning: number;
  /** Engaged under 5s — the visit never really started. */
  bounced: number;
  avgActiveMs: number;
  medianActiveMs: number | null;
  /** Average of the deepest point each session saw, per-mille of the page. */
  avgScroll: number;
  ctaSessions: number;
  ctaClicks: number;
  clicks: number;
  rageSessions: number;
  deadClicks: number;
  videoSessions: number;
  errorSessions: number;
  avgLoadMs: number | null;
  avgLcpMs: number | null;
  avgBootMs: number | null;
  /** Median of the same — a few visitors who leave a tab in the background
   *  drag the average to many seconds, so the diagnosis reads this one. */
  medianBootMs: number | null;
  avgFirstCtaMs: number | null;
  /** When the earliest session in the window started — the tracker may be
   *  younger than the window, and sales must not be counted from before it. */
  firstSessionAt: string | null;
}

export interface InsightsSection {
  key: string;
  order: number;
  /** Sessions that reached the section. */
  reached: number;
  /** Engaged ms while it held the centre of the screen, averaged over the
   *  sessions that reached it. */
  avgMs: number;
  clicks: number;
  ctaClicks: number;
}

export interface InsightsClick {
  path: string;
  label: string;
  href: string;
  kind: InsightsClickKind;
  section: string;
  clicks: number;
  sessions: number;
  rage: number;
}

/** One point of the heatmap: an element (or, failing that, a page position) and
 *  how often it was clicked there. Offsets are per-mille. */
export interface InsightsHeatPoint {
  path: string;
  ex: number;
  ey: number;
  x: number;
  y: number;
  n: number;
  kind: InsightsClickKind;
}

export interface InsightsBreakdownRow {
  key: string;
  sessions: number;
  bounced: number;
  avgActiveMs: number;
  avgScroll: number;
  ctaSessions: number;
  avgLoadMs: number | null;
}

export interface InsightsDay {
  day: string;
  sessions: number;
  ctaSessions: number;
  avgActiveMs: number;
}

/** Orders for one campaign (or ad) key. */
export interface InsightsSalesRow {
  key: string;
  orders: number;
  paid: number;
  revenue: number;
}

/** Orders on the pages that frame this copy (Supabase orders, by page_id). */
export interface InsightsSales {
  paidOrders: number;
  revenue: number;
  currency: string | null;
  /** Orders created (paid or not) — checkout submitted. */
  orders: number;
  /** Start of the order window: the window's start, or the first tracked
   *  session if that is later, so orders and sessions cover the same span. */
  since: string;
  byCampaign: InsightsSalesRow[];
  byContent: InsightsSalesRow[];
}

/**
 * What a page's "Belanja Meta" is out of. The page counts only its own campaigns
 * from the hour tracking began, so next to Ads Manager it reads low; these are
 * the account-wide figures that close the gap.
 */
export interface InsightsSpendScope {
  /** The first hour the page's spend is counted from (UTC, on the hour). */
  from: string;
  /** Every ad in every account the token reads, over those same hours. */
  accountWindow: number;
  /** Every ad since 00:00 today in Malaysia — Ads Manager's "Today". */
  accountToday: number;
  /** When spend was last copied from Meta. */
  syncedAt: string | null;
}

/** Meta spend for one campaign (or ad) over the window (D1 insight_ad_spend). */
export interface InsightsSpendRow {
  key: string;
  spend: number;
  metaPurchases: number;
  currency: string | null;
}

/** One campaign (or ad): what its visitors did on the page, and what they paid. */
export interface InsightsCampaignRow {
  key: string;
  label: string | null;
  sessions: number;
  bounced: number;
  avgScroll: number;
  ctaSessions: number;
  orders: number;
  paid: number;
  revenue: number;
  /** Meta spend over the same hours; null when Meta has none on record for it
   *  (another ad account, an affiliate's own ad, no token). */
  spend: number | null;
  /** Meta's own purchase count, for comparison with `paid`. */
  metaPurchases: number | null;
}

/** Operator names for campaign / ad ids (D1 insight_labels). */
export interface InsightsLabels {
  campaign: Record<string, string>;
  ad: Record<string, string>;
}

/** Sessions with no campaign / no ad, as the breakdown queries name them. */
/**
 * A version of a copy other than the page itself (A): its Versi B ("b"), or a
 * funnel's AI-written version ("ai…"). The tracker records it on every session
 * and click, so each version's numbers and heatmap read apart.
 */
export const AI_VERSION_RE = /^ai[0-9a-z]{4,16}$/;
export const INSIGHT_VERSION_RE = /^(?:b|ai[0-9a-z]{4,16})$/;
export const isInsightVersion = (v: unknown): v is string => typeof v === "string" && INSIGHT_VERSION_RE.test(v);

/** One entry of a copy's version picker: "a" is the page itself. */
export interface InsightsVersion {
  key: string;
  label: string;
  sessions: number;
  /** When the version's edits last changed; null for A or a version with no stored file. */
  updatedAt: string | null;
  /** Whether a version file is stored (a version can have visits after its file is gone). */
  stored: boolean;
  /** Whether a funnel slot serves this version; one that is not gets no visitors. A is always true. */
  inFunnel: boolean;
}

export const NO_CAMPAIGN = "(tiada utm_campaign)";
export const NO_AD = "(tiada utm_content)";

/**
 * Put each order's campaign (D1 insight_order_touch) onto the order read from
 * Supabase. An order with no touch is organic, or its touch was never written —
 * either way it counts under "no campaign", never under someone else's — unless
 * its buyer arrived on a Facebook click (`fbc`), which counts as FB_TEXT_CAMPAIGN.
 */
export function attachTouches<T extends { id: string; fbc?: string | null; created_at?: string }>(
  orders: T[],
  touches: { order_id: string; utm_campaign: string | null; utm_content: string | null }[],
  adKeys?: Map<string, MetaAdKeyTarget>,
): (T & { utm_campaign: string | null; utm_content: string | null; via_fbclid?: true })[] {
  const byId = new Map(touches.map((t) => [t.order_id, t]));
  return orders.map((o) => {
    const t = byId.get(o.id);
    const content = t?.utm_content || null;
    const untaggedFb = !t?.utm_campaign && !content && !!o.created_at && isUntaggedFbOrder(o.fbc, o.created_at);
    const campaign = t?.utm_campaign || (untaggedFb ? FB_TEXT_CAMPAIGN : null);
    const known = campaign === FB_TEXT_CAMPAIGN ? fbOrderTarget(o.fbc, adKeys) : null;
    if (known) return { ...o, utm_campaign: known.campaignId, utm_content: known.adId, via_fbclid: true as const };
    return { ...o, utm_campaign: campaign, utm_content: content };
  });
}

/** The ad and campaign Meta's ad key stands for (server/lib/metaAdKeys). */
export interface MetaAdKeyTarget {
  adId: string;
  campaignId: string;
}

/** A text-link order's ad and campaign, when its buyer's fbc carries a key Sellhiz has learned. */
export function fbOrderTarget(fbc: string | null | undefined, adKeys?: Map<string, MetaAdKeyTarget>): MetaAdKeyTarget | null {
  if (!adKeys?.size) return null;
  const key = metaAdKeyFromFbc(fbc);
  return (key && adKeys.get(key)) || null;
}

/** One order as the tally reads it from Supabase `orders`. */
export interface TallyOrderRow {
  amount: number | string | null;
  currency: string | null;
  payment_status: string;
  utm_campaign: string | null;
  utm_content: string | null;
}

/** Totals and per campaign / per ad, from the window's orders. */
export function salesFromOrders(orders: TallyOrderRow[], since: string): InsightsSales {
  const group = (key: (o: TallyOrderRow) => string) => {
    const m = new Map<string, InsightsSalesRow>();
    for (const o of orders) {
      const k = key(o);
      const r = m.get(k) ?? { key: k, orders: 0, paid: 0, revenue: 0 };
      r.orders++;
      if (o.payment_status === "paid") {
        r.paid++;
        r.revenue = Math.round((r.revenue + Number(o.amount ?? 0)) * 100) / 100;
      }
      m.set(k, r);
    }
    return [...m.values()];
  };
  const paid = orders.filter((o) => o.payment_status === "paid");
  return {
    orders: orders.length,
    paidOrders: paid.length,
    revenue: Math.round(paid.reduce((sum, o) => sum + Number(o.amount ?? 0), 0) * 100) / 100,
    currency: paid[0]?.currency ?? orders[0]?.currency ?? null,
    since,
    byCampaign: group((o) => o.utm_campaign || NO_CAMPAIGN),
    byContent: group((o) => o.utm_content || NO_AD),
  };
}

/**
 * Join behaviour (sessions by campaign) with sales (orders by campaign) into one
 * row per key, best seller first. A key present on only one side still gets a
 * row — an order whose visit predates tracking, or a campaign with no sale yet.
 */
export function mergeCampaignRows(
  sessions: InsightsBreakdownRow[],
  sales: InsightsSalesRow[],
  labels: Record<string, string>,
  spend: InsightsSpendRow[] = [],
): InsightsCampaignRow[] {
  const rows = new Map<string, InsightsCampaignRow>();
  const row = (key: string) => {
    let r = rows.get(key);
    if (!r) {
      r = {
        key,
        label: labels[key] ?? null,
        sessions: 0,
        bounced: 0,
        avgScroll: 0,
        ctaSessions: 0,
        orders: 0,
        paid: 0,
        revenue: 0,
        spend: null,
        metaPurchases: null,
      };
      rows.set(key, r);
    }
    return r;
  };
  for (const s of sessions) Object.assign(row(s.key), { sessions: s.sessions, bounced: s.bounced, avgScroll: s.avgScroll, ctaSessions: s.ctaSessions });
  for (const o of sales) Object.assign(row(o.key), { orders: o.orders, paid: o.paid, revenue: o.revenue });
  // Spend only lands on rows this copy already has: the ad account also runs
  // campaigns for other pages, and those are not this page's cost.
  for (const m of spend) {
    const r = rows.get(m.key);
    if (r) Object.assign(r, { spend: m.spend, metaPurchases: m.metaPurchases });
  }
  return [...rows.values()].sort((a, b) => b.paid - a.paid || b.revenue - a.revenue || b.sessions - a.sessions);
}

/** Revenue ÷ spend, or null when there is no spend to divide by. */
export const roasOf = (r: Pick<InsightsCampaignRow, "revenue" | "spend">) =>
  r.spend && r.spend > 0 ? r.revenue / r.spend : null;
/** Spend ÷ paid orders, or null when nothing was paid (or no spend is known). */
export const costPerPaidOf = (r: Pick<InsightsCampaignRow, "paid" | "spend">) =>
  r.spend != null && r.paid > 0 ? r.spend / r.paid : null;

export interface InsightsOverview {
  slug: string;
  from: string;
  to: string;
  device: InsightsDevice | "all";
  totals: InsightsTotals;
  /** Sessions by engaged time: <5s, 5–15s, 15–30s, 30–60s, 1–3min, 3min+. */
  activeBuckets: number[];
  /** Share of sessions whose viewport reached each 5% of the page (20 points,
   *  5% … 100%). */
  reach: number[];
  /** Average engaged ms per session spent with each 5% band at screen centre. */
  attention: number[];
  sections: InsightsSection[];
  topClicks: InsightsClick[];
  byDevice: InsightsBreakdownRow[];
  bySource: InsightsBreakdownRow[];
  byInApp: InsightsBreakdownRow[];
  byCampaign: InsightsBreakdownRow[];
  byContent: InsightsBreakdownRow[];
  daily: InsightsDay[];
  sales: InsightsSales | null;
  /** Campaigns and ads with behaviour and real sales side by side. */
  campaigns: InsightsCampaignRow[];
  ads: InsightsCampaignRow[];
  labels: InsightsLabels;
  /** New campaign/ad ids are named from Meta by the cron (a token is set). */
  labelsAuto?: boolean;
  /** Which version these numbers are for: "a" (the page itself), "b", "ai…". */
  version?: string;
  /** Where the window actually started (later than `days` ago when read since a version's edit). */
  windowFrom?: string;
  /** When the version's edits last changed, when read for a version. */
  versionEditedAt?: string | null;
  /** Account-wide spend beside the page's, once Meta spend is on record. */
  spendScope?: InsightsSpendScope | null;
}

export interface InsightsSlugRow {
  slug: string;
  sessions: number;
  lastAt: string;
}

/** Engaged-time bucket edges in ms, matching `activeBuckets`. */
export const ACTIVE_BUCKET_EDGES = [5_000, 15_000, 30_000, 60_000, 180_000];
export const ACTIVE_BUCKET_LABELS = ["<5s", "5–15s", "15–30s", "30–60s", "1–3 min", "3 min+"];

/** `reach` from a histogram of `max_scroll / 50` (0…20): a session that saw 60%
 *  of the page counts towards every point up to 60%. */
export function reachFromHistogram(hist: { b: number; n: number }[], sessions: number): number[] {
  const counts = new Array<number>(21).fill(0);
  for (const { b, n } of hist) counts[Math.max(0, Math.min(20, b))] += n;
  const reach = new Array<number>(20).fill(0);
  let atOrAbove = 0;
  for (let k = 20; k >= 1; k--) {
    atOrAbove += counts[k];
    reach[k - 1] = sessions ? atOrAbove / sessions : 0;
  }
  return reach;
}

export type InsightsSeverity = "critical" | "warning" | "info" | "good";

/** Where a fix is carried out: a panel further down the console, or the copy's
 *  own WP-import panels. The console turns each into a button. */
export type InsightsFixLink =
  | { kind: "heatmap"; mode: "click" | "scroll" | "attention"; section?: string }
  | { kind: "funnel" }
  | { kind: "clicks" }
  | { kind: "campaigns" }
  | { kind: "text" }
  | { kind: "perf" };

export interface InsightsFinding {
  id: string;
  severity: InsightsSeverity;
  title: string;
  detail: string;
  /** The fix as steps, in this page's own sections and numbers. */
  steps: string[];
  /** What "fixed" reads as on this screen, or null when no number is fair. */
  target: string | null;
  links: InsightsFixLink[];
  /** The work to hand an AI for this issue — the task of `findingPrompt`.
   *  Null when there is nothing to write yet. */
  ask: string | null;
}

/** Below this many sessions a percentage is one visitor's mood, not a finding. */
export const INSIGHTS_MIN_SESSIONS = 30;

const SEVERITY_ORDER: Record<InsightsSeverity, number> = { critical: 0, warning: 1, info: 2, good: 3 };
const SEVERITY_LABEL: Record<InsightsSeverity, string> = { critical: "Kritikal", warning: "Amaran", info: "Info", good: "Baik" };
const pctText = (f: number) => `${Math.round(f * 100)}%`;
const secText = (ms: number) => `${(ms / 1000).toFixed(1)}s`;
const share = (part: number, whole: number) => (whole ? part / whole : 0);

/** The question a FAQ click opened, from a `summary · …` / `details · …` label. */
function faqQuestion(c: InsightsClick): string | null {
  const m = /^(summary|details|button|h3|h4)\s·\s(.+)$/.exec(c.label);
  if (!m) return null;
  if (m[1] !== "summary" && m[1] !== "details" && !/faq|soalan/i.test(c.section)) return null;
  return m[2];
}

/** The section that carries the price: pricing/harga/pakej first, offer as a fallback. */
function pricingSection(sections: InsightsSection[]): InsightsSection | null {
  return (
    sections.find((s) => /pric|harga|pakej/i.test(s.key)) ?? sections.find((s) => /offer|tawaran/i.test(s.key)) ?? null
  );
}

/**
 * The numbers two versions of a page are compared on, as fractions of sessions.
 * Null where there is nothing to measure (no sessions, no price section, no
 * store page): a version with no visitors has no rate, not a rate of 0%.
 */
export interface VersionMetrics {
  sessions: number;
  /** Paid orders per session — what the owner judges a version on. */
  paidCr: number | null;
  pricingReach: number | null;
  ctaRate: number | null;
  avgScroll: number | null;
  bottomReach: number | null;
  bounceRate: number | null;
  medianActiveMs: number | null;
}

export function versionMetrics(o: InsightsOverview): VersionMetrics {
  const t = o.totals;
  const n = t.sessions;
  if (!n) {
    return { sessions: 0, paidCr: null, pricingReach: null, ctaRate: null, avgScroll: null, bottomReach: null, bounceRate: null, medianActiveMs: null };
  }
  const pricing = pricingSection(o.sections);
  return {
    sessions: n,
    paidCr: o.sales && n ? o.sales.paidOrders / n : null,
    pricingReach: pricing && n ? pricing.reached / n : null,
    ctaRate: share(t.ctaSessions, n),
    avgScroll: t.avgScroll / 1000,
    bottomReach: o.reach[18] ?? 0,
    bounceRate: share(t.bounced, n),
    medianActiveMs: t.medianActiveMs,
  };
}

/**
 * Turn one copy's numbers into the handful of things worth fixing, worst first,
 * each with the evidence, the steps to fix it and the number that shows it worked.
 * Rule-based on purpose: every finding can be traced to a number on the same screen.
 */
export function diagnoseInsights(o: InsightsOverview): InsightsFinding[] {
  const t = o.totals;
  const n = t.sessions;
  if (n < INSIGHTS_MIN_SESSIONS) {
    return [
      {
        id: "sample",
        severity: "info",
        title: `Data belum cukup (${n} sesi)`,
        detail: `Diagnosis bermula selepas ${INSIGHTS_MIN_SESSIONS} sesi supaya peratus tidak terpesong oleh beberapa pelawat.`,
        steps: ["Tunggu trafik masuk, atau pilih tempoh yang lebih panjang."],
        target: null,
        links: [],
        ask: null,
      },
    ];
  }
  const out: InsightsFinding[] = [];
  const bounceRate = share(t.bounced, n);
  const nameOf = (kind: "campaign" | "ad", key: string) => o.labels?.[kind]?.[key] ?? key;
  // The campaign most of the traffic comes from — the ad whose promise the
  // first screen has to keep.
  const topCampaign = o.byCampaign
    .filter((r) => r.key !== NO_CAMPAIGN && share(r.sessions, n) >= 0.1)
    .sort((a, b) => b.sessions - a.sessions)[0];
  const adPromise = topCampaign
    ? ` kempen "${nameOf("campaign", topCampaign.key)}" (${pctText(share(topCampaign.sessions, n))} trafik)`
    : " iklan yang paling banyak bawa trafik";

  // 1. Where the page loses the most readers between one section and the next.
  const secs = o.sections;
  let worst: { from: InsightsSection; to: InsightsSection; drop: number; index: number } | null = null;
  for (let i = 1; i < secs.length; i++) {
    const drop = share(secs[i - 1].reached, n) - share(secs[i].reached, n);
    if (!worst || drop > worst.drop) worst = { from: secs[i - 1], to: secs[i], drop, index: i };
  }
  if (worst && worst.drop >= 0.25) {
    const early = worst.index <= 2;
    const from = worst.from.key;
    const to = worst.to.key;
    const toShare = share(worst.to.reached, n);
    out.push({
      id: "section-drop",
      severity: worst.drop >= 0.4 ? "critical" : "warning",
      title: `${pctText(worst.drop)} pelawat berhenti selepas seksyen "${from}"`,
      detail: `${pctText(share(worst.from.reached, n))} sampai "${from}", hanya ${pctText(toShare)} teruskan ke "${to}". Ini kebocoran terbesar antara dua seksyen.`,
      steps: early
        ? [
            `Padankan tajuk "${from}" dengan janji${adPromise}: pelawat mesti nampak perkara yang sama dengan yang mereka klik.`,
            "Bawa satu bukti (jumlah pembeli, testimoni, hasil) dan butang CTA ke skrin pertama.",
            `Uji satu hook lain selama 3–7 hari, kemudian bandingkan berapa ramai teruskan ke "${to}".`,
          ]
        : [
            `Baca seksyen "${from}" seperti pelawat di telefon: terlalu panjang, atau tak jawab "apa untung saya"?`,
            "Pendekkan kepada isi paling kuat, atau pecahkan kepada poin dan gambar.",
            `Letak satu bukti dan butang CTA sebelum "${from}", supaya yang berhenti di situ sudah nampak tawaran.`,
          ],
      target: `≥ ${pctText(toShare + worst.drop / 2)} teruskan ke "${to}" (sekarang ${pctText(toShare)})`,
      links: [{ kind: "heatmap", mode: early ? "scroll" : "attention", section: from }, { kind: "funnel" }, { kind: "text" }],
      ask: early
        ? `Tulis 3 versi skrin pertama (tajuk, subtajuk, satu baris bukti, teks butang CTA) yang sepadan dengan janji${adPromise}, dan cadangkan susunan 5 seksyen pertama.`
        : `Tulis semula seksyen "${from}" dalam versi separuh panjang, dan cadangkan di mana letak satu bukti dan satu butang CTA sebelum seksyen itu.`,
    });
  }

  // 2. Visits that never really started.
  if (bounceRate >= 0.3) {
    out.push({
      id: "bounce",
      severity: bounceRate >= 0.5 ? "critical" : "warning",
      title: `${pctText(bounceRate)} pelawat keluar dalam 5 saat pertama`,
      detail: `${t.bounced} daripada ${n} sesi aktif kurang 5 saat. Median masa aktif ${t.medianActiveMs != null ? secText(t.medianActiveMs) : "—"}.`,
      steps: [
        "Buka halaman dari iklan pada telefon (dalam app FB/IG) dan kira berapa saat sebelum tajuk dan gambar pertama muncul.",
        `Pastikan gambar dan tajuk pertama sama dengan kreatif${adPromise}.`,
        "Buang apa-apa yang menolak kandungan ke bawah pada skrin pertama (banner, popup, logo besar).",
      ],
      target: `Keluar < 5s di bawah 30% (sekarang ${pctText(bounceRate)})`,
      links: [{ kind: "heatmap", mode: "scroll" }, { kind: "perf" }],
      ask: "Senaraikan sebab paling mungkin pelawat keluar dalam 5 saat pada halaman ini, kemudian tulis 3 versi skrin pertama yang difahami dalam sekali pandang (tajuk ≤ 8 patah perkataan, subtajuk, teks butang CTA).",
    });
  }

  // 3. How long the sales page takes to appear after the ad click. The median,
  // not the average: one backgrounded tab reads as a minute-long load.
  const startMs = t.medianBootMs ?? t.avgLoadMs;
  if (startMs != null && startMs >= 3000) {
    const inApp = o.byInApp.filter((r) => r.key !== "browser").reduce((a, r) => a + r.sessions, 0);
    out.push({
      id: "slow-start",
      severity: startMs >= 5000 ? "critical" : "warning",
      title: `Separuh pelawat tunggu lebih ${secText(startMs)} sebelum halaman jualan muncul`,
      detail: `${t.medianBootMs != null ? "Median dari klik sampai halaman sedia dalam kedai" : "Masa muat salinan"}.${share(inApp, n) >= 0.3 ? ` ${pctText(share(inApp, n))} pelawat guna pelayar dalam app (FB/IG), yang lebih perlahan.` : ""}`,
      steps: [
        'Di panel Prestasi salinan ini, hidupkan "Lazy-load imej", "Lazy-play video" dan "Buang kod lama selamat".',
        "Kecilkan imej hero (WebP, bawah 150 KB) dan jangan muatkan video atau skrip pihak ketiga pada skrin pertama.",
        "Uji semula dalam pelayar FB/IG di telefon menggunakan data mudah alih, bukan Wi-Fi.",
      ],
      target: `Median halaman sedia di bawah 3.0s (sekarang ${secText(startMs)})`,
      links: [{ kind: "perf" }],
      ask: "Beri senarai semak teknikal untuk turunkan masa skrin pertama halaman ini ke bawah 3 saat dalam pelayar dalam app Facebook/Instagram, disusun ikut kesan paling besar dahulu.",
    });
  }

  // 4. Does traffic reach the price, and does it buy once there?
  const pricing = pricingSection(secs);
  if (pricing) {
    const reach = share(pricing.reached, n);
    const ctaAtPrice = share(t.ctaSessions, pricing.reached);
    if (reach < 0.3) {
      out.push({
        id: "pricing-reach",
        severity: reach < 0.15 ? "critical" : "warning",
        title: `Hanya ${pctText(reach)} pelawat sampai ke harga`,
        detail: `${pricing.reached} daripada ${n} sesi sampai seksyen "${pricing.key}".`,
        steps: [
          "Letak ringkasan harga dan butang beli selepas hero atau bukti pertama, bukan hanya di hujung.",
          `Tambah butang "Lihat harga" yang lompat terus ke "${pricing.key}" dalam 2–3 seksyen pertama.`,
          "Akhiri setiap seksyen yang panjang dengan satu CTA kecil.",
        ],
        target: `≥ 30% pelawat sampai ke harga (sekarang ${pctText(reach)})`,
        links: [{ kind: "heatmap", mode: "scroll", section: pricing.key }, { kind: "funnel" }, { kind: "text" }],
        ask: `Tulis satu blok ringkasan harga yang pendek (nama pakej, harga, 3 manfaat, teks butang) untuk diletak selepas hero, dan cadangkan 2 tempat lain untuk butang yang lompat ke "${pricing.key}".`,
      });
    }
    if (pricing.reached >= 10 && ctaAtPrice >= 0.25) {
      out.push({
        id: "price-converts",
        severity: "good",
        title: `Yang sampai harga, ${pctText(Math.min(1, ctaAtPrice))} klik beli`,
        detail: `${t.ctaSessions} sesi klik CTA berbanding ${pricing.reached} yang sampai "${pricing.key}".`,
        steps: [
          "Jangan ubah harga atau pakej: tawaran ini sudah menukar.",
          `Fokus membawa lebih ramai sampai ke "${pricing.key}": CTA lebih awal, seksyen sebelum harga lebih pendek.`,
        ],
        target: `Kekalkan ≥ ${pctText(Math.min(1, ctaAtPrice))} klik beli di harga sambil lebih ramai sampai ke situ`,
        links: [{ kind: "heatmap", mode: "scroll", section: pricing.key }, { kind: "funnel" }],
        ask: `Cadangkan cara membawa lebih ramai pelawat ke seksyen "${pricing.key}" tanpa mengubah tawaran: apa yang boleh dipendekkan, dibuang atau dipindah sebelum harga.`,
      });
    }
  }

  // 5. What the readers worry about: the FAQ items they open.
  const faq = o.topClicks
    .map((c) => ({ q: faqQuestion(c), clicks: c.clicks }))
    .filter((x): x is { q: string; clicks: number } => !!x.q);
  const faqTotal = faq.reduce((a, x) => a + x.clicks, 0);
  if (faqTotal >= 3) {
    const top = faq.slice(0, 3);
    const asked = top.map((x) => `"${x.q}"`).join(", ");
    const faqSection = secs.find((s) => /faq|soalan/i.test(s.key));
    out.push({
      id: "objections",
      severity: "info",
      title: "Soalan yang paling dibuka pelawat",
      detail: top.map((x) => `"${x.q}" (${x.clicks}×)`).join(" · "),
      steps: [
        `Ini bantahan sebenar. Jawab ${asked} dalam 1–2 ayat terus di bawah harga atau butang beli.`,
        `Jangan simpan jawapan di FAQ sahaja${faqSection ? `: hanya ${pctText(share(faqSection.reached, n))} pelawat sampai ke situ` : ""}.`,
        "Jadikan soalan yang paling kerap dibuka sebagai bukti (gambar, contoh, jaminan) dalam seksyen yang lebih awal.",
      ],
      target: null,
      links: [{ kind: "clicks" }, { kind: "text" }],
      ask: `Tulis jawapan ringkas (maksimum 2 ayat setiap satu) untuk soalan-soalan ini, dan cadangkan di mana setiap jawapan diletak berhampiran harga atau CTA: ${asked}.`,
    });
  }

  // 6. Clicks on things that do nothing, and frustration.
  // At least three different visitors: one person's triple-tap is not a pattern.
  const dead = o.topClicks.find(
    (c) => c.kind === "dead" && c.sessions >= 3 && (c.clicks >= 5 || share(c.sessions, n) >= 0.02),
  );
  if (dead) {
    const where = dead.section ? ` dalam seksyen "${dead.section}"` : "";
    out.push({
      id: "dead-click",
      severity: "warning",
      title: `"${dead.label}" diklik ${dead.clicks}× tapi tak buat apa-apa`,
      detail: `${dead.sessions} sesi${dead.section ? `, dalam seksyen "${dead.section}"` : ""}. Pelawat sangka ia butang.`,
      steps: [
        "Jika elemen ini patut membawa ke tawaran, jadikan ia pautan ke harga atau checkout.",
        "Jika tidak, ubah rupanya (buang bayang atau warna butang) supaya tak nampak boleh diklik.",
      ],
      target: "Klik mati pada elemen ini jatuh ke sifar dalam 7 hari",
      links: [{ kind: "heatmap", mode: "click", ...(dead.section ? { section: dead.section } : {}) }, { kind: "clicks" }],
      ask: `Elemen "${dead.label}"${where} diklik ${dead.clicks}× oleh ${dead.sessions} pelawat tetapi tak buat apa-apa. Cadangkan sama ada ia patut jadi butang (berserta teks butangnya dan ke mana ia pergi) atau diubah rupa, dengan sebabnya.`,
    });
  }
  if (t.rageSessions >= 3 && share(t.rageSessions, n) >= 0.02) {
    const raged = o.topClicks
      .filter((c) => c.rage > 0)
      .sort((a, b) => b.rage - a.rage)
      .slice(0, 3)
      .map((c) => `"${c.label || c.path}"${c.section ? ` (seksyen "${c.section}")` : ""}`);
    out.push({
      id: "rage",
      severity: "warning",
      title: `${pctText(share(t.rageSessions, n))} sesi ada klik geram (rage click)`,
      detail: `${t.rageSessions} sesi klik berulang kali di tempat yang sama dalam masa singkat.`,
      steps: [
        "Buka heatmap mod Klik dan cari kawasan yang paling merah.",
        `Semak elemen dengan lajur Rage tinggi dalam "Elemen paling diklik"${raged.length ? `: ${raged.join(", ")}` : ""}.`,
        "Biasanya punca ialah butang yang lambat, gambar yang disangka galeri, atau pautan rosak. Betulkan, kemudian uji di telefon.",
      ],
      target: `Sesi rage di bawah 2% (sekarang ${pctText(share(t.rageSessions, n))})`,
      links: [{ kind: "heatmap", mode: "click" }, { kind: "clicks" }],
      ask: `Pelawat klik berulang kali dengan geram pada${raged.length ? ` ${raged.join(", ")}` : " beberapa elemen"}. Terangkan punca yang paling mungkin untuk setiap satu dan cara membetulkannya.`,
    });
  }
  if (t.errorSessions >= 3 && share(t.errorSessions, n) >= 0.05) {
    out.push({
      id: "errors",
      severity: "warning",
      title: `${pctText(share(t.errorSessions, n))} sesi ada ralat halaman atau imej gagal dimuat`,
      detail: `${t.errorSessions} sesi.`,
      steps: [
        "Buka halaman pada telefon dengan Console (Safari Web Inspector atau Chrome remote debugging).",
        "Cari imej atau skrip yang gagal (404, disekat), kemudian muat naik semula atau buang.",
        "Simpan semula salinan supaya cache dibersihkan, dan buka sekali lagi untuk sahkan.",
      ],
      target: `Sesi beralat di bawah 5% (sekarang ${pctText(share(t.errorSessions, n))})`,
      links: [{ kind: "perf" }],
      ask: "Beri langkah demi langkah untuk mencari imej atau skrip yang gagal dimuat pada halaman ini di pelayar telefon, dan cara membetulkan setiap jenis ralat yang biasa.",
    });
  }

  // 7. Traffic that behaves worse than the rest — by source, then by ad.
  const weak = (rows: InsightsBreakdownRow[], what: string, id: string, name: (k: string) => string = (k) => k) => {
    const candidates = rows
      .filter((r) => r.sessions >= 20 && share(r.sessions, n) >= 0.1 && !/^\(tiada/.test(r.key))
      .map((r) => ({ r, b: share(r.bounced, r.sessions) }))
      .filter(({ b }) => b >= bounceRate * 1.25 && b >= bounceRate + 0.1)
      .sort((a, b) => b.b - a.b);
    const hit = candidates[0];
    if (!hit) return;
    const label = `${what} "${name(hit.r.key)}"`;
    out.push({
      id,
      severity: "warning",
      title: `${label} bawa pelawat yang cepat keluar`,
      detail: `${pctText(hit.b)} keluar < 5s berbanding ${pctText(bounceRate)} purata (${hit.r.sessions} sesi, CTA ${pctText(share(hit.r.ctaSessions, hit.r.sessions))}).`,
      steps: [
        `Buka kreatif ${label} dan halaman ini sebelah-menyebelah: janji, gambar dan tawaran mesti sama.`,
        "Sebelum ubah bajet, bandingkan bayaran sebenar dalam Prestasi kempen dan dalam Ads Manager pada jam yang sama.",
        "Jika padanan memang lemah, tukar kreatif atau hantar trafik ini ke halaman yang sepadan dengan janjinya.",
      ],
      target: `Keluar < 5s untuk trafik ini turun ke purata halaman, ${pctText(bounceRate)} (sekarang ${pctText(hit.b)})`,
      links: id === "weak-source" ? [{ kind: "funnel" }] : [{ kind: "campaigns" }],
      ask: `${label} bawa pelawat yang keluar lebih cepat (${pctText(hit.b)} berbanding ${pctText(bounceRate)} purata). Tulis 3 versi teks iklan yang lebih sepadan dengan skrin pertama halaman ini, ATAU satu versi skrin pertama khas untuk trafik ini.`,
    });
  };
  weak(o.byContent, "Iklan", "weak-ad", (k) => nameOf("ad", k));
  weak(o.byCampaign, "Kempen", "weak-campaign", (k) => nameOf("campaign", k));
  weak(o.bySource, "Sumber", "weak-source");

  // 8. Real sales per campaign (orders + their ad click), not Meta's estimate —
  //    and, when Meta spend is on record, whether each campaign pays for itself.
  const campaigns = (o.campaigns ?? []).filter((r) => r.key !== NO_CAMPAIGN);
  const nameOfRow = (r: InsightsCampaignRow) => r.label ?? r.key;
  const rm = (v: number) => `RM${Math.round(v).toLocaleString("en-MY")}`;
  const withSpend = campaigns.filter((r) => r.spend != null && r.spend >= 30);
  if (withSpend.length) {
    // Meta spend is on record: judge campaigns by what they return per ringgit.
    const byRoas = withSpend
      .filter((r) => r.paid >= 2)
      .map((r) => ({ r, roas: r.revenue / (r.spend as number) }))
      .sort((a, b) => b.roas - a.roas);
    if (byRoas.length) {
      const { r, roas } = byRoas[0];
      const name = nameOfRow(r);
      const cpp = rm((r.spend as number) / r.paid);
      out.push({
        id: "best-campaign",
        severity: "good",
        title: `Kempen terbaik: "${name}" — ROAS ${roas.toFixed(1)}×`,
        detail: `Hasil sebenar ${rm(r.revenue)} daripada belanja ${rm(r.spend as number)}; ${r.paid} dibayar, kos ${cpp} setiap bayaran.${byRoas.length > 1 ? ` Seterusnya: "${nameOfRow(byRoas[1].r)}" ${byRoas[1].roas.toFixed(1)}×.` : ""}`,
        steps: [
          "Naikkan bajet kempen ini 20–30% setiap 3 hari selagi ROAS dan kos setiap bayaran kekal stabil.",
          "Bandingkan kreatifnya dengan kempen yang rugi, dan salin sudut atau hook yang menang.",
        ],
        target: `Kekalkan ROAS ≥ ${roas.toFixed(1)}× semasa bajet naik`,
        links: [{ kind: "campaigns" }],
        ask: `Kempen "${name}" paling untung (ROAS ${roas.toFixed(1)}×, kos ${cpp} setiap bayaran). Cadangkan 3 variasi kreatif baharu dengan sudut yang sama untuk diuji, dan pelan menaikkan bajet yang selamat.`,
      });
    }
    const losing = withSpend
      .filter((r) => (r.spend as number) >= 50 && r.revenue < (r.spend as number))
      .sort((a, b) => ((b.spend as number) - b.revenue) - ((a.spend as number) - a.revenue))[0];
    if (losing) {
      const name = nameOfRow(losing);
      const roas = losing.revenue / (losing.spend as number);
      out.push({
        id: "losing-campaign",
        severity: "warning",
        title: `"${name}" rugi: belanja ${rm(losing.spend as number)}, hasil sebenar ${rm(losing.revenue)} (ROAS ${roas.toFixed(1)}×)`,
        detail: `${losing.paid} dibayar daripada ${losing.sessions} sesi${losing.metaPurchases != null ? `; Meta mengira ${losing.metaPurchases} purchase` : ""}.`,
        steps: [
          "Banding dengan Ads Manager pada jam yang sama: Meta mungkin mengira pembelian dari tontonan atau klik lebih awal yang tiada di sini.",
          "Jika masih rugi, semak padanan kreatif dan audiens dengan skrin pertama halaman, atau kurangkan bajetnya.",
          "ROAS bawah 1× ialah rugi pada jualan depan — kecuali upsell selepasnya menutup jurang. Jangan pause berdasarkan angka di sini sahaja.",
        ],
        target: `ROAS ≥ 1.0× (sekarang ${roas.toFixed(1)}×)`,
        links: [{ kind: "campaigns" }],
        ask: `Kempen "${name}" belanja ${rm(losing.spend as number)} tetapi hasil sebenar hanya ${rm(losing.revenue)} (ROAS ${roas.toFixed(1)}×; Meta kira ${losing.metaPurchases ?? "?"} purchase, ${losing.paid} benar-benar dibayar). Senaraikan punca yang mungkin ikut keutamaan dan apa yang perlu diubah pada kreatif, audiens atau halaman sebelum kurangkan bajet.`,
      });
    }
  } else {
    // No spend on record: rank by how many sessions become payments.
    const ranked = campaigns
      .filter((r) => r.sessions >= 30 && r.paid >= 2)
      .map((r) => ({ r, cr: r.paid / r.sessions }))
      .sort((a, b) => b.cr - a.cr);
    if (ranked.length) {
      const { r, cr } = ranked[0];
      const name = nameOfRow(r);
      out.push({
        id: "best-campaign",
        severity: "good",
        title: `Kempen terbaik: "${name}" — ${(cr * 100).toFixed(1)}% sesi jadi bayaran`,
        detail: `${r.paid} dibayar daripada ${r.sessions} sesi (RM${Math.round(r.revenue)}), keluar < 5s ${pctText(share(r.bounced, r.sessions))}.${ranked.length > 1 ? ` Seterusnya: "${nameOfRow(ranked[1].r)}" ${(ranked[1].cr * 100).toFixed(1)}%.` : ""}`,
        steps: [
          "Naikkan bajet kempen ini 20–30% setiap 3 hari selagi kos setiap bayaran kekal stabil.",
          "Bandingkan kreatifnya dengan kempen yang lemah, dan salin sudut atau hook yang menang.",
        ],
        target: `Kekalkan ≥ ${(cr * 100).toFixed(1)}% sesi jadi bayaran semasa bajet naik`,
        links: [{ kind: "campaigns" }],
        ask: `Kempen "${name}" paling baik menukar (${(cr * 100).toFixed(1)}% sesi jadi bayaran). Cadangkan 3 variasi kreatif baharu dengan sudut yang sama untuk diuji, dan pelan menaikkan bajet yang selamat.`,
      });
    }
  }
  const dry = campaigns.filter((r) => r.sessions >= 50 && r.paid === 0).sort((a, b) => b.sessions - a.sessions)[0];
  if (dry && o.sales) {
    const name = dry.label ?? dry.key;
    const cta = pctText(share(dry.ctaSessions, dry.sessions));
    const scroll = `${Math.round(dry.avgScroll / 10)}%`;
    out.push({
      id: "campaign-no-sales",
      severity: "warning",
      title: `"${name}": ${dry.sessions} sesi, tiada bayaran${dry.spend != null ? ` (belanja ${rm(dry.spend)})` : ""}`,
      detail: `Sejak penjejakan pesanan bermula dalam tempoh ini. Klik beli ${cta}, scroll purata ${scroll}.`,
      steps: [
        "Semak kempen ini dalam Ads Manager pada jam yang sama: Meta mungkin mengira pembelian dari tontonan atau klik lebih awal yang tiada di sini.",
        "Jika Meta juga tiada pembelian, semak padanan iklan dengan skrin pertama, atau kurangkan bajetnya.",
        "Jangan pause berdasarkan angka di sini sahaja.",
      ],
      target: null,
      links: [{ kind: "campaigns" }],
      ask: `Kempen "${name}" bawa ${dry.sessions} sesi tetapi tiada bayaran (klik beli ${cta}, scroll purata ${scroll}${dry.spend != null ? `, belanja ${rm(dry.spend)}` : ""}). Senaraikan punca yang mungkin ikut keutamaan, dan apa yang perlu disemak dalam Ads Manager sebelum ubah bajet.`,
    });
  }

  // 9. After the click: does the checkout keep them?
  const sales = o.sales;
  if (sales && t.ctaSessions >= 10) {
    const ordered = share(sales.orders, t.ctaSessions);
    if (ordered < 0.5) {
      out.push({
        id: "checkout-leak",
        severity: "warning",
        title: `${t.ctaSessions} sesi klik beli, hanya ${sales.orders} pesanan dibuat`,
        detail: `${pctText(Math.min(1, ordered))} daripada klik CTA jadi pesanan, dalam tempoh yang sama.`,
        steps: [
          "Cuba checkout sendiri dari pelayar FB/IG di telefon, dan kira masa sehingga borang muncul.",
          "Kurangkan medan borang kepada yang wajib sahaja.",
          "Pastikan butang setiap pakej membawa ke pakej yang betul.",
        ],
        target: `≥ 50% klik beli jadi pesanan (sekarang ${pctText(Math.min(1, ordered))})`,
        links: [{ kind: "clicks" }],
        ask: "Senaraikan punca biasa pelawat klik beli tetapi tidak menghantar borang checkout produk digital di Malaysia (pelayar dalam app, FPX, medan borang), dan apa yang perlu diuji pada halaman ini.",
      });
    }
    if (sales.orders >= 10 && share(sales.paidOrders, sales.orders) < 0.6) {
      out.push({
        id: "unpaid",
        severity: "warning",
        title: `${sales.orders - sales.paidOrders} daripada ${sales.orders} pesanan tidak dibayar`,
        detail: `Hanya ${pctText(share(sales.paidOrders, sales.orders))} pesanan sampai bayar.`,
        steps: [
          "Uji gateway (FPX dan kad) dengan satu bayaran sebenar.",
          "Hidupkan susulan pesanan belum bayar dalam tetapan kedai.",
          "Tawarkan kaedah bayaran kedua jika yang pertama gagal.",
        ],
        target: `≥ 60% pesanan dibayar (sekarang ${pctText(share(sales.paidOrders, sales.orders))})`,
        links: [],
        ask: "Tulis 2 mesej susulan (satu emel, satu WhatsApp) untuk pembeli yang membuat pesanan tetapi belum bayar: nada mesra, ringkas, dengan [PAUTAN BAYAR].",
      });
    }
  }

  return out.sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
}

/** `"hero"` → `"hero" (the heading on the page)`, once the heatmap frame has read
 *  the page's headings. Section keys alone mean nothing to a reader. */
export function nameSections(text: string, labels: Record<string, string>): string {
  return text.replace(/"([^"]+)"/g, (whole, key: string) => (labels[key] ? `"${key}" (${labels[key]})` : whole));
}

export interface FindingPromptContext {
  /** Section headings by key, from the heatmap frame. */
  labels: Record<string, string>;
  pageTitle?: string | null;
  /** An absolute URL that shows the page without counting as a visit. */
  pageUrl?: string | null;
  days: number;
}

/**
 * One finding as a prompt to paste into an AI: the page, its numbers, the issue,
 * the fix so far and the work wanted. Plain text the operator can edit before
 * copying. Null for a finding with nothing to write (`ask` is null).
 */
export function findingPrompt(f: InsightsFinding, o: InsightsOverview, ctx: FindingPromptContext): string | null {
  if (!f.ask) return null;
  const t = o.totals;
  const n = t.sessions;
  const name = (s: string) => nameSections(s, ctx.labels);
  const lines = [
    "Anda pakar conversion (CRO) untuk sales page produk digital di Malaysia. Bantu saya betulkan SATU isu pada sales page saya, berdasarkan data pelawat sebenar di bawah.",
    "",
    "## Halaman",
    `- Halaman: ${ctx.pageTitle ? `${ctx.pageTitle} ` : ""}(salinan WP-import Sellhiz, slug "${o.slug}")`,
  ];
  if (ctx.pageUrl) lines.push(`- URL: ${ctx.pageUrl}`);
  lines.push(
    `- Data: ${ctx.days} hari terakhir, ${o.device === "all" ? "semua peranti" : o.device}, ${n} sesi`,
    `- Ringkasan: keluar < 5s ${pctText(share(t.bounced, n))} · median masa aktif ${t.medianActiveMs != null ? secText(t.medianActiveMs) : "—"} · klik beli ${pctText(share(t.ctaSessions, n))}${o.sales ? ` · ${o.sales.paidOrders} bayaran (CR ${(share(o.sales.paidOrders, n) * 100).toFixed(1)}%)` : ""}`,
  );
  if (o.sections.length) {
    lines.push("", "## Susunan seksyen (% pelawat yang sampai)");
    o.sections.slice(0, 15).forEach((s, i) =>
      lines.push(`${i + 1}. ${s.key}${ctx.labels[s.key] ? ` "${ctx.labels[s.key]}"` : ""}: ${pctText(share(s.reached, n))}`),
    );
  }
  lines.push("", `## Isu (${SEVERITY_LABEL[f.severity]})`, name(f.title), name(f.detail));
  if (f.steps.length) {
    lines.push("", "## Cadangan awal");
    f.steps.forEach((s, i) => lines.push(`${i + 1}. ${name(s)}`));
  }
  if (f.target) lines.push(`Sasaran: ${name(f.target)}`);
  lines.push(
    "",
    "## Tugasan",
    name(f.ask),
    "",
    "## Syarat",
    "- Bahasa Melayu santai, ayat pendek, sesuai dibaca di telefon.",
    "- Jangan reka angka, testimoni atau janji yang tiada dalam halaman. Tulis [ISI] di tempat yang perlukan data sebenar.",
    "- Jangan ubah harga atau pakej kecuali saya minta.",
    "- Akhiri dengan angka dalam Analitik Halaman yang patut berubah selepas 7 hari jika pembetulan ini berkesan.",
  );
  return lines.join("\n");
}

/** The ad click a checkout is credited to: its utm_* values, as posted with the order. */
export interface CampaignTouch {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  /** Meta's {{placement}} (Instagram_Reels, Facebook_Mobile_Feed…), when the ad's URL tags carry it. */
  utmPlacement?: string;
  /** Meta's own key for the ad behind the click, from its fbclid (metaAdKeyFromFbclid). */
  metaAdKey?: string;
}

/** Twice Meta's default 7-day click window: the operator chose not to lose slower buyers. */
export const CAMPAIGN_TOUCH_TTL_MS = 14 * 86_400_000;

/**
 * The campaign key for a Facebook click that carried no campaign or ad id. A
 * link typed into an ad's text gets Meta's `fbclid` but never the ad's URL
 * parameters, so these are ad clicks whose campaign only Meta knows.
 */
export const FB_TEXT_CAMPAIGN = "__fb_text__";
export const FB_TEXT_LABEL = "Klik Facebook tanpa ID kempen (pautan teks iklan)";

const CLICK_IDS = ["fbclid", "gclid", "gbraid", "wbraid", "ttclid"];

/**
 * The campaign a landing URL carries, or null when it carries none. Only a URL
 * with a campaign or ad id counts: a bare `utm_source` (a link in a bio, a
 * WhatsApp share) must not overwrite the ad click that is still being credited.
 * A fresh Facebook click with neither does replace it, as FB_TEXT_CAMPAIGN: it is
 * the latest ad click, and keeping the older campaign would credit the wrong one.
 */
export function campaignTouchFromSearch(search: string, now: number): (CampaignTouch & { at: number }) | null {
  let p: URLSearchParams;
  try {
    p = new URLSearchParams(search);
  } catch {
    return null;
  }
  const pick = (k: string) => p.get(k)?.trim().slice(0, 100) || undefined;
  const touch = {
    utmSource: pick("utm_source"),
    utmMedium: pick("utm_medium"),
    utmCampaign: pick("utm_campaign"),
    utmContent: pick("utm_content"),
    utmPlacement: pick("utm_placement"),
    metaAdKey: metaAdKeyFromFbclid(p.get("fbclid")) ?? undefined,
  };
  if (!touch.utmCampaign && !touch.utmContent) {
    if (!p.get("fbclid")) return null;
    return {
      utmSource: touch.utmSource ?? "facebook",
      utmMedium: touch.utmMedium,
      utmCampaign: FB_TEXT_CAMPAIGN,
      metaAdKey: touch.metaAdKey,
      at: now,
    };
  }
  return { ...touch, at: now };
}

/**
 * Meta's own key for the ad behind a click, read from a newer fbclid: two marker
 * characters, then base64url of fields (`extn`, `aem`, …, `adid` + 8 bytes, …)
 * before `_aem_`. It is NOT the Ads Manager ad id and Meta does not document it,
 * but it is fixed per ad, so the ad it stands for is learned from clicks that also
 * carried utm_content (meta_ad_keys). Null for older fbclids (`IwAR…`) and junk.
 */
export function metaAdKeyFromFbclid(fbclid: string | null | undefined): string | null {
  if (!fbclid || fbclid.length < 24) return null;
  const body = fbclid.split("_aem_")[0].slice(2).replace(/-/g, "+").replace(/_/g, "/");
  let bin: string;
  try {
    bin = atob(body.slice(0, body.length - (body.length % 4)));
  } catch {
    return null;
  }
  const at = bin.indexOf("adid");
  if (at < 0 || bin.length < at + 12) return null;
  let id = 0n;
  for (let i = at + 4; i < at + 12; i++) id = (id << 8n) | BigInt(bin.charCodeAt(i) & 0xff);
  const key = id.toString();
  return /^\d{10,20}$/.test(key) ? key : null;
}

/** The same key from a stored `_fbc` value (`fb.<n>.<ms>.<fbclid>`). */
export function metaAdKeyFromFbc(fbc: string | null | undefined): string | null {
  const m = /^fb\.\d+\.\d+\.(.+)$/.exec(fbc ?? "");
  return m ? metaAdKeyFromFbclid(m[1]) : null;
}

/**
 * The Meta ad key a visit is credited to: its own fbclid's, or — when it came with
 * nothing of its own — the remembered click's, matching withCampaignTouch.
 */
export function visitMetaAdKey(search: string, touch: CampaignTouch): string | null {
  let p: URLSearchParams;
  try {
    p = new URLSearchParams(search);
  } catch {
    return null;
  }
  const fbclid = p.get("fbclid");
  if (fbclid) return metaAdKeyFromFbclid(fbclid);
  if (p.get("utm_campaign") || p.get("utm_content") || CLICK_IDS.some((k) => p.get(k))) return null;
  return touch.metaAdKey ?? null;
}

/**
 * The storefront query a framed copy's tracker should see. A visit that arrived
 * with no campaign of its own and no fresh ad click is credited to the stored
 * ad click, the same one its order would be credited to.
 */
export function withCampaignTouch(search: string, touch: CampaignTouch): string {
  let p: URLSearchParams;
  try {
    p = new URLSearchParams(search);
  } catch {
    return search;
  }
  if (p.get("utm_campaign") || p.get("utm_content") || CLICK_IDS.some((k) => p.get(k))) return search;
  if (!touch.utmCampaign && !touch.utmContent) return search;
  for (const [k, v] of [
    ["utm_source", touch.utmSource],
    ["utm_medium", touch.utmMedium],
    ["utm_campaign", touch.utmCampaign],
    ["utm_content", touch.utmContent],
  ] as const) {
    if (v) p.set(k, v);
    else p.delete(k);
  }
  return p.toString();
}

/** When the click behind a `_fbc` value (`fb.<n>.<ms>.<fbclid>`) happened, in ms. */
export function fbcClickedAt(fbc: string | null | undefined): number | null {
  const m = /^fb\.\d+\.(\d{10,14})\./.exec(fbc ?? "");
  return m ? Number(m[1]) : null;
}

/**
 * An order with no campaign whose buyer came from a Facebook click inside the
 * window: written before FB_TEXT_CAMPAIGN existed, or with storage blocked.
 */
export function isUntaggedFbOrder(fbc: string | null | undefined, createdAt: string): boolean {
  const at = fbcClickedAt(fbc);
  const ordered = Date.parse(createdAt);
  if (at == null || !Number.isFinite(ordered)) return false;
  return at <= ordered + 5 * 60_000 && ordered - at <= CAMPAIGN_TOUCH_TTL_MS;
}

/** A touch as the browser keeps it: the click's utm values and when it was seen. */
export type StoredCampaignTouch = CampaignTouch & { at: number };

const TOUCH_KEYS = ["utmSource", "utmMedium", "utmCampaign", "utmContent", "utmPlacement"] as const;

function liveTouch(t: (CampaignTouch & { at?: unknown }) | null, now: number): StoredCampaignTouch | null {
  if (!t || typeof t.at !== "number" || now - t.at > CAMPAIGN_TOUCH_TTL_MS || now < t.at) return null;
  const out: StoredCampaignTouch = { at: t.at };
  for (const k of TOUCH_KEYS) {
    if (typeof t[k] === "string" && t[k]) out[k] = (t[k] as string).slice(0, 100);
  }
  if (typeof t.metaAdKey === "string" && /^\d{10,20}$/.test(t.metaAdKey)) out.metaAdKey = t.metaAdKey;
  return out.utmCampaign || out.utmContent ? out : null;
}

function parseTouch(stored: string | null, now: number): StoredCampaignTouch | null {
  if (!stored) return null;
  try {
    return liveTouch(JSON.parse(stored) as CampaignTouch & { at?: unknown }, now);
  } catch {
    return null;
  }
}

const withoutAt = (t: StoredCampaignTouch | null): CampaignTouch => {
  const out: CampaignTouch = {};
  for (const k of TOUCH_KEYS) if (t?.[k]) out[k] = t[k];
  if (t?.metaAdKey) out.metaAdKey = t.metaAdKey;
  return out;
};

/** A stored touch, if it is still inside the attribution window. Last click wins. */
export function liveCampaignTouch(stored: string | null, now: number): CampaignTouch {
  return withoutAt(parseTouch(stored, now));
}

/**
 * The touch an order is credited to: the newer of the one in storage and the one
 * this page load captured itself. Storage alone is not enough — Facebook's
 * iPhone browser handed about half its checkouts an empty store (9827, 7 Oct)
 * while the same visit's landing had carried its campaign.
 */
export function pickCampaignTouch(stored: string | null, memory: StoredCampaignTouch | null, now: number): CampaignTouch {
  const a = parseTouch(stored, now);
  const b = liveTouch(memory, now);
  return withoutAt(a && b ? (b.at > a.at ? b : a) : (a ?? b));
}

/** What a landing query carries: a campaign or ad id ("c"), only a Facebook click id ("f"), or neither ("-"). */
export function searchClickKind(search: string): "c" | "f" | "-" {
  try {
    const p = new URLSearchParams(search);
    if (p.get("utm_campaign") || p.get("utm_content")) return "c";
    return p.get("fbclid") ? "f" : "-";
  } catch {
    return "-";
  }
}

export interface TouchDiagInput {
  /** The checkout runs inside an iframe. */
  frame: boolean;
  /** What storage held before this checkout looked again: a touch, nothing, or it threw. */
  storage: "ok" | "none" | "err";
  /** This page load captured a touch itself. */
  memory: boolean;
  /** What the checkout's own URL carries, and what the page load's first URL carried. */
  url: "c" | "f" | "-";
  boot: "c" | "f" | "-";
  /** The `_fbc` cookie is there. */
  fbc: boolean;
  /** Seconds since this document started. */
  ageS: number;
  /** navigate / reload / back_forward / prerender, as the browser reports it. */
  nav: string | null;
  /** Where the credited touch came from: storage, this load's memory, the URL now, or nowhere. */
  src: "s" | "m" | "u" | "-";
}

/**
 * One short line kept with the order's touch in D1 (insight_order_touch.diag), so
 * an order that reaches the tally without its ad click says why. Fixed order,
 * no personal data.
 */
export function touchDiag(d: TouchDiagInput): string {
  const age = Number.isFinite(d.ageS) ? Math.max(0, Math.round(d.ageS)) : -1;
  const nav = (d.nav ?? "-").replace(/[^a-z_]/g, "").slice(0, 12) || "-";
  return `fr${d.frame ? 1 : 0} st:${d.storage} me${d.memory ? 1 : 0} url:${d.url} boot:${d.boot} fbc${d.fbc ? 1 : 0} age:${age} nav:${nav} src:${d.src}`;
}
